// Appointments: a customer booked to come in at a time, for 30 to 90 minutes,
// optionally with one of the store's staff. Kept in the customer's book as kind
// APPT (server: addFashionClientItem with startAt/endAt/staffId,
// getFashionAppointments for a store's day, getFashionStoreStaff for who can be
// picked, closeFashionClientItem DONE or CAN). The server checks the same rules
// as validateAppointmentDraft below, and that the staff member is free.
import { MAX_CLIENT_TEXT, readClientItems } from "./clientBook";

export const APPOINTMENT_LENGTHS = [30, 60, 90];
export const DEFAULT_LENGTH = 60;
export const MAX_APPOINTMENT_MINUTES = 240;
export const MAX_APPOINTMENT_DAYS = 365;
// A booking a few minutes late (the customer is at the counter) still goes in.
export const APPOINTMENT_GRACE_MINUTES = 10;
export const SLOT_MINUTES = 30;

const clean = (s) => String(s ?? "").replace(/["\\]/g, "").replace(/[\r\n]+/g, " ").trim();
const str = (s) => `"${clean(s)}"`;
const pad = (v) => String(v).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoMinute = (d) => `${isoDay(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const MINUTE_MS = 60 * 1000;
const REPLY = "{ status message recordId documentNo }";
const FIELDS = "itemId kind customerId customerName mobileNo bunitId storeName text dueDate status outcome created closedAt startAt endAt staffId staffName";

export const buildAppointmentsQuery = (bunitId, day) => ({
  query: `query { getFashionAppointments(bunitId: ${str(bunitId)}${day ? `, day: ${str(day)}` : ""}) { ${FIELDS} }}`,
});

export const buildStoreStaffQuery = (bunitId) => ({ query: `query { getFashionStoreStaff(bunitId: ${str(bunitId)}) { id name }}` });

export function buildAddAppointmentMutation(item) {
  const fields = [
    `bunitId: ${str(item.bunitId)}`,
    `customerId: ${str(item.customerId)}`,
    `kind: "APPT"`,
    `startAt: ${str(item.startAt)}`,
    `endAt: ${str(item.endAt)}`,
    item.staffId ? `staffId: ${str(item.staffId)}` : null,
    item.text ? `text: ${str(item.text)}` : null,
  ].filter(Boolean);
  return { query: `mutation { addFashionClientItem(item: { ${fields.join(", ")} }) ${REPLY} }` };
}

// Server response → appointments, soonest first, or null when the call failed.
export function readAppointments(response) {
  const rows = readClientItems(response, "getFashionAppointments");
  if (!rows) return null;
  return rows.filter((r) => r.kind === "APPT").sort(byStart);
}

// Server response → [{ id, name }] by name, or null when the call failed.
export function readStaff(response) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.getFashionStoreStaff;
  if (rows == null) return [];
  if (!Array.isArray(rows)) return null;
  const seen = new Set();
  return rows
    .filter((s) => s?.id && !seen.has(s.id) && seen.add(s.id))
    .map((s) => ({ id: s.id, name: clean(s.name) || "Staff member" }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export const byStart = (a, b) => String(a.startAt || "").localeCompare(String(b.startAt || "")) || String(a.endAt || "").localeCompare(String(b.endAt || ""));

// "2026-10-27T11:30" → a Date, or null.
export function parseMinute(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(s || ""));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return Number.isNaN(d.getTime()) || d.getDate() !== Number(m[3]) || d.getHours() !== Number(m[4]) ? null : d;
}

// The form's day, start time and length → { startAt, endAt } as the server
// takes them ("YYYY-MM-DDTHH:mm"), or null when the day or time is unusable.
export function appointmentTimes(day, time, minutes) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day || "")) || !/^\d{2}:\d{2}$/.test(String(time || ""))) return null;
  const start = parseMinute(`${day}T${time}`);
  const len = Number(minutes);
  if (!start || !Number.isFinite(len)) return null;
  return { startAt: isoMinute(start), endAt: isoMinute(new Date(start.getTime() + len * MINUTE_MS)) };
}

// The next half hour from now ("14:05" → "14:30"), for the form's start time,
// and the day it falls on.
export function nextSlot(now = new Date()) {
  const d = new Date(now.getTime());
  d.setSeconds(0, 0);
  const over = d.getMinutes() % SLOT_MINUTES;
  d.setMinutes(d.getMinutes() + (over ? SLOT_MINUTES - over : 0));
  return { day: isoDay(d), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

export const lastAppointmentDay = (now = new Date()) => isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + MAX_APPOINTMENT_DAYS));

// The book form → { item } ready for buildAddAppointmentMutation, or { error }
// in the server's words.
export function validateAppointmentDraft(draft, now = new Date()) {
  if (!draft?.customerId) return { error: "Pick the customer first." };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(draft.day || "")) || !/^\d{2}:\d{2}$/.test(String(draft.time || ""))) return { error: "Pick the day and time of the appointment." };
  const minutes = Number(draft.minutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return { error: "Say how long the appointment is." };
  const times = appointmentTimes(draft.day, draft.time, minutes);
  if (!times) return { error: "Pick the day and time of the appointment." };
  const start = parseMinute(times.startAt);
  if (start.getTime() < now.getTime() - APPOINTMENT_GRACE_MINUTES * MINUTE_MS) return { error: "The appointment can't be in the past." };
  if (draft.day > lastAppointmentDay(now)) return { error: "Book within a year from now." };
  if (minutes > MAX_APPOINTMENT_MINUTES) return { error: "An appointment can be at most 4 hours." };
  const text = clean(draft.note);
  if (text.length > MAX_CLIENT_TEXT) return { error: `Keep it to ${MAX_CLIENT_TEXT} characters or fewer.` };
  return { item: { bunitId: draft.bunitId, customerId: draft.customerId, kind: "APPT", startAt: times.startAt, endAt: times.endAt, staffId: draft.staffId || null, text } };
}

// "11:30" from "2026-10-27T11:30".
export const timeOf = (s) => (/T(\d{2}:\d{2})/.exec(String(s || "")) || [])[1] || "";

// "11:30 – 12:30" for a row; just the start when the end is missing.
export function timeRange(item) {
  const from = timeOf(item?.startAt);
  const to = timeOf(item?.endAt);
  if (!from) return "";
  return to ? `${from} – ${to}` : from;
}

// "Today 11:30", "Tomorrow 16:00", or "Sat 31 Oct 11:30" for the customer screen.
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function whenLabel(startAt, now = new Date()) {
  const d = parseMinute(startAt);
  if (!d) return "";
  const time = timeOf(startAt);
  const day = isoDay(d);
  if (day === isoDay(now)) return `Today ${time}`;
  if (day === isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1))) return `Tomorrow ${time}`;
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${time}`;
}

export const dayOf = (startAt) => String(startAt || "").slice(0, 10);

// "Today", "Tomorrow", "Yesterday" or "Sat 31 Oct" for the day stepper.
export function dayTitle(day, now = new Date()) {
  const d = parseMinute(`${day}T00:00`);
  if (!d) return "";
  const rel = Math.round((d.getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / (24 * 60 * MINUTE_MS));
  if (rel === 0) return "Today";
  if (rel === 1) return "Tomorrow";
  if (rel === -1) return "Yesterday";
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function shiftDay(day, by) {
  const d = parseMinute(`${day}T00:00`);
  return d ? isoDay(new Date(d.getFullYear(), d.getMonth(), d.getDate() + by)) : day;
}

export const APPOINTMENT_STATUS = { OPEN: "Booked", DONE: "Done", CAN: "Cancelled" };
export const appointmentStatus = (item) => APPOINTMENT_STATUS[item?.status] || item?.status || "";

// A booked appointment that has started and not been closed is running late
// to close; one whose end has passed is missed until marked done or cancelled.
export function appointmentTone(item, now = new Date()) {
  if (item?.status === "DONE") return "ok";
  if (item?.status !== "OPEN") return "neu";
  const end = parseMinute(item.endAt) || parseMinute(item.startAt);
  if (end && end.getTime() < now.getTime()) return "warn";
  return "info";
}

// The customer's own open appointments still to come (or still on today),
// soonest first.
export function upcomingAppointments(rows, now = new Date()) {
  const today = isoDay(now);
  return (rows || []).filter((r) => r.kind === "APPT" && r.status === "OPEN" && dayOf(r.startAt) >= today).sort(byStart);
}

// The day list's summary: how many booked, done and cancelled.
export function appointmentStats(rows) {
  const s = { booked: 0, done: 0, cancelled: 0 };
  (rows || []).forEach((r) => {
    if (r.status === "OPEN") s.booked += 1;
    else if (r.status === "DONE") s.done += 1;
    else s.cancelled += 1;
  });
  return s;
}
